import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
} from 'material-react-table';
import {
  Box, Button, createTheme, ThemeProvider, useTheme,
  FormControl, InputLabel, Select, MenuItem, Checkbox,
  TextField,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import VehicleHistorySummary from './VehicleHistorySummary';
import {
  getReportVehicles,
} from '../../actions/apiGetInfo';
import { convertSecondsToTime, noteStamp } from '../../utils/dateFunctions';
import LoadingSpinner from '../../components/LoadingSpinner/loadingSpinner';
import _ from 'lodash';

// Helper function to render truncated selected items with ellipsis
const renderTruncatedValue = (selected, options) => {
  if (selected.length === 0) return '';
  const maxDisplay = 3;
  const displayItems = selected.slice(0, maxDisplay).map((item) => {
    const option = options.find((opt) => opt.value === item);
    return option ? option.label : item;
  });
  return displayItems.join(', ') + (selected.length > maxDisplay ? '...' : '');
};

// Helper function to convert hours to seconds
const hoursToSeconds = (hours) => {
  const [hourPart, minutePart = '0'] = hours.split(':').map(Number);
  return (hourPart * 3600) + (minutePart * 60);
};

const columnHelper = createMRTColumnHelper();

const columns = [
  columnHelper.accessor('stock', {
    header: 'Stock',
    size: 30,
    minSize: 30,
  }),
  columnHelper.accessor('year', {
    header: 'Year',
    size: 20,
    minSize: 20,
    filterFn: (row, id, filterValue) => {
      if (!filterValue || filterValue.length === 0) return false;
      const rowValue = String(row.original[id]);
      if (typeof rowValue !== 'string' || rowValue === 'undefined' || rowValue === 'null') return false;
      const lowerRowValue = rowValue.toLowerCase();
      return filterValue.some(val => String(val).toLowerCase() === lowerRowValue);
    },
    Filter: ({ column }) => {
      const options = column.columnDef.filterSelectOptions || [];
      const selectedValues = column.getFilterValue() || [];
      return (
        <FormControl fullWidth sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            multiple
            value={selectedValues}
            onChange={(e) => {
              const value = e.target.value;
              if (value.includes('deselect-all')) {
                column.setFilterValue([]);
              } else if (value.includes('select-all')) {
                column.setFilterValue(options.map(opt => opt.value.toString()));
              } else {
                column.setFilterValue(value);
              }
            }}
            label="Year"
            renderValue={(selected) => renderTruncatedValue(selected, options)}
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 300 },
              },
            }}
          >
            <MenuItem value="select-all">Select All</MenuItem>
            <MenuItem value="deselect-all">Deselect All</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value.toString()}>
                <Checkbox checked={selectedValues.includes(option.value.toString())} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  }),
  columnHelper.accessor('make', {
    header: 'Make',
    filterFn: (row, id, filterValue) => {
      if (!filterValue || filterValue.length === 0) return false;
      const rowValue = row.original[id];
      if (typeof rowValue !== 'string' || rowValue === undefined || rowValue === null) return false;
      const lowerRowValue = rowValue.toLowerCase();
      return filterValue.some(val => typeof val === 'string' && val.toLowerCase() === lowerRowValue);
    },
    Filter: ({ column }) => {
      const options = column.columnDef.filterSelectOptions || [];
      const selectedValues = column.getFilterValue() || [];
      return (
        <FormControl fullWidth sx={{ minWidth: 120 }}>
          <InputLabel>Make</InputLabel>
          <Select
            multiple
            value={selectedValues}
            onChange={(e) => {
              const value = e.target.value;
              if (value.includes('deselect-all')) {
                column.setFilterValue([]);
              } else if (value.includes('select-all')) {
                column.setFilterValue(options.map(opt => opt.value));
              } else {
                column.setFilterValue(value);
              }
            }}
            label="Make"
            renderValue={(selected) => renderTruncatedValue(selected, options)}
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 300 },
              },
            }}
          >
            <MenuItem value="select-all">Select All</MenuItem>
            <MenuItem value="deselect-all">Deselect All</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedValues.includes(option.value)} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  }),
  columnHelper.accessor('model', {
    header: 'Model',
    filterFn: (row, id, filterValue) => {
      if (!filterValue || filterValue.length === 0) return false;
      const rowValue = row.original[id];
      if (typeof rowValue !== 'string' || rowValue === undefined || rowValue === null) return false;
      const lowerRowValue = rowValue.toLowerCase();
      return filterValue.some(val => typeof val === 'string' && val.toLowerCase() === lowerRowValue);
    },
    Filter: ({ column }) => {
      const options = column.columnDef.filterSelectOptions || [];
      const selectedValues = column.getFilterValue() || [];
      return (
        <FormControl fullWidth sx={{ minWidth: 120 }}>
          <InputLabel>Model</InputLabel>
          <Select
            multiple
            value={selectedValues}
            onChange={(e) => {
              const value = e.target.value;
              if (value.includes('deselect-all')) {
                column.setFilterValue([]);
              } else if (value.includes('select-all')) {
                column.setFilterValue(options.map(opt => opt.value));
              } else {
                column.setFilterValue(value);
              }
            }}
            label="Model"
            renderValue={(selected) => renderTruncatedValue(selected, options)}
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 300 },
              },
            }}
          >
            <MenuItem value="select-all">Select All</MenuItem>
            <MenuItem value="deselect-all">Deselect All</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedValues.includes(option.value)} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  }),
  columnHelper.accessor('starttime', {
    header: 'StartTime (gt)',
    size: 122,
    minSize: 122,
    muiTableHeadCellProps: { sx: { width: '122px !important', maxWidth: '122px !important', padding: '6px' } },
    muiTableBodyCellProps: { sx: { width: '122px !important', maxWidth: '122px !important', padding: '6px' } },
    filterFn: (row, id, filterValue) => {
      const rowValue = new Date(row.original[id]);
      if (isNaN(rowValue.getTime())) return true;
      const start = filterValue ? new Date(filterValue) : null;
      if (!start || isNaN(start.getTime())) return true;
      return rowValue >= start;
    },
    Cell: ({ row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span>{noteStamp(row.original.starttime) || 'N/A'}</span>
      </Box>
    ),
    Filter: ({ column }) => {
      const [startTime, setStartTime] = useState('');
      const handleChange = (e) => {
        const value = e.target.value;
        setStartTime(value);
        column.setFilterValue(value || undefined);
      };

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
          <TextField
            label="gt Start Time"
            type="datetime-local"
            value={startTime}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1 }}
          />
        </Box>
      );
    },
    sortingFn: (rowA, rowB) => {
      const timeA = new Date(rowA.original.starttime).getTime() || 0;
      const timeB = new Date(rowB.original.starttime).getTime() || 0;
      return timeA - timeB;
    },
  }),
  columnHelper.accessor('endtime', {
    header: 'EndTime (lt)',
    size: 122,
    minSize: 122,
    muiTableHeadCellProps: { sx: { width: '122px !important', maxWidth: '122px !important', padding: '6px' } },
    muiTableBodyCellProps: { sx: { width: '122px !important', maxWidth: '122px !important', padding: '6px' } },
    filterFn: (row, id, filterValue) => {
      const rowValue = new Date(row.original[id]);
      if (isNaN(rowValue.getTime())) return true;
      const end = filterValue ? new Date(filterValue) : null;
      if (!end || isNaN(end.getTime())) return true;
      return rowValue <= end;
    },
    Cell: ({ row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span>{noteStamp(row.original.endtime) || 'N/A'}</span>
      </Box>
    ),
    Filter: ({ column }) => {
      const [endTime, setEndTime] = useState('');
      const handleChange = (e) => {
        const value = e.target.value;
        setEndTime(value);
        column.setFilterValue(value || undefined);
      };

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
          <TextField
            label="lt End Time"
            type="datetime-local"
            value={endTime}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1 }}
          />
        </Box>
      );
    },
    sortingFn: (rowA, rowB) => {
      const timeA = new Date(rowA.original.endtime).getTime() || 0;
      const timeB = new Date(rowB.original.endtime).getTime() || 0;
      return timeA - timeB;
    },
  }),
  columnHelper.accessor('time', {
    header: 'Time (gt/lt)',
    size: 40,
    minSize: 40,
    filterFn: (row, id, filterValue) => {
      if (!filterValue || !filterValue.value) return true;
      const seconds = row.original.time_seconds || 0;
      const thresholdSeconds = hoursToSeconds(filterValue.value);
      return filterValue.operator === 'gt' ? seconds > thresholdSeconds : seconds < thresholdSeconds;
    },
    Filter: ({ column }) => {
      const [isFilterVisible, setIsFilterVisible] = useState(false);
      const [operator, setOperator] = useState('gt');
      const [hours, setHours] = useState('');

      const handleHeaderClick = () => setIsFilterVisible(!isFilterVisible);

      return (
        <Box sx={{ minWidth: isFilterVisible ? '120px' : '40px' }}>
          {isFilterVisible && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <FormControl sx={{ minWidth: 60 }}>
                <InputLabel>Op</InputLabel>
                <Select value={operator} onChange={(e) => { setOperator(e.target.value); column.setFilterValue({ operator: e.target.value, value: hours } || undefined); }} label="Op" size="small">
                  <MenuItem value="gt">gt</MenuItem>
                  <MenuItem value="lt">lt</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Hours (e.g., 1:30)"
                value={hours}
                onChange={(e) => { setHours(e.target.value); column.setFilterValue({ operator, value: e.target.value } || undefined); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 80, maxWidth: 80 }}
                size="small"
              />
            </Box>
          )}
          {!isFilterVisible && (
            <Box
              sx={{
                cursor: 'pointer',
                padding: '4px',
                textAlign: 'center',
                backgroundColor: isFilterVisible ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              }}
              onClick={handleHeaderClick}
            >
              Show Filter
            </Box>
          )}
        </Box>
      );
    },
    Cell: ({ row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span>{row.original.time || 'N/A'}</span>
      </Box>
    ),
    sortingFn: (rowA, rowB) => {
      const timeA = rowA.original.time_seconds || 0;
      const timeB = rowB.original.time_seconds || 0;
      return timeA - timeB;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('total', {
    header: 'Total (gt/lt)',
    size: 40,
    minSize: 40,
    filterFn: (row, id, filterValue) => {
      if (!filterValue || !filterValue.value) return true;
      const seconds = row.original.total_seconds || 0;
      const thresholdSeconds = hoursToSeconds(filterValue.value);
      return filterValue.operator === 'gt' ? seconds > thresholdSeconds : seconds < thresholdSeconds;
    },
    Filter: ({ column }) => {
      const [isFilterVisible, setIsFilterVisible] = useState(false);
      const [operator, setOperator] = useState('gt');
      const [hours, setHours] = useState('');

      const handleHeaderClick = () => setIsFilterVisible(!isFilterVisible);

      return (
        <Box sx={{ minWidth: isFilterVisible ? '120px' : '40px' }}>
          {isFilterVisible && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <FormControl sx={{ minWidth: 60 }}>
                <InputLabel>Op</InputLabel>
                <Select value={operator} onChange={(e) => { setOperator(e.target.value); column.setFilterValue({ operator: e.target.value, value: hours } || undefined); }} label="Op" size="small">
                  <MenuItem value="gt">gt</MenuItem>
                  <MenuItem value="lt">lt</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Hours (e.g., 1:30)"
                value={hours}
                onChange={(e) => { setHours(e.target.value); column.setFilterValue({ operator, value: e.target.value } || undefined); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 80, maxWidth: 80 }}
                size="small"
              />
            </Box>
          )}
          {!isFilterVisible && (
            <Box
              sx={{
                cursor: 'pointer',
                padding: '4px',
                textAlign: 'center',
                backgroundColor: isFilterVisible ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              }}
              onClick={handleHeaderClick}
            >
              Show Filter
            </Box>
          )}
        </Box>
      );
    },
    Cell: ({ row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span>{row.original.total || 'N/A'}</span>
      </Box>
    ),
    sortingFn: (rowA, rowB) => {
      const totalA = rowA.original.total_seconds || 0;
      const totalB = rowB.original.total_seconds || 0;
      return totalA - totalB;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('entries', {
    header: 'Entries',
    size: 20,
    minSize: 20,
  }),
];

const exportableColumnKeys = ['stock', 'year', 'make', 'model', 'starttime', 'endtime', 'time', 'total', 'entries'];

const renderDetailPanel = ({ row }) => (
  <Box
    sx={{
      display: 'flex',
      padding: '16px',
    }}
  >
    <VehicleHistorySummary vehicle={row.original.vehicle[0]} vehicle_id={row.original.vehicle[0].id} is_single />
  </Box>
);

const now = new Date();
const month = (now.getMonth() + 1).toString().padStart(2, '0');
const day = now.getDate().toString().padStart(2, '0');
const year = now.getFullYear();
const filename = `report_vehicles_${month}_${day}_${year}`;

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
  filename,
});

const ReportVehicles = ({
  report_id,
  department_id,
  work_flow_id,
  vehicles,
  departmentIds,
}) => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();
  const report_vehicles = useSelector((state) => state.settings_reports.report_vehicles || []);

  // Fetch data only if vehicles prop is not provided and report_id is valid
  useEffect(() => {
    if (!vehicles && report_id > 0) {
      dispatch(getReportVehicles(report_id));
    }
  }, [dispatch, report_id, department_id, vehicles]);


  const tableTheme = useMemo(() => createTheme({
    palette: {
      mode: 'light',
    },
  }), [globalTheme]);

  // Use vehicles prop if provided, otherwise fall back to report_vehicles
  const dataSource = vehicles || report_vehicles;

  // Prepare unique filter options for Year, Make, and Model
  const filterOptions = useMemo(() => {
    if (!dataSource || dataSource.length === 0) return { years: [], makes: [], models: [] };
    const years = new Set();
    const makes = new Set();
    const models = new Set();
    dataSource.forEach((vehicle) => {
      if (vehicle.year) years.add(vehicle.year);
      if (vehicle.make) makes.add(vehicle.make);
      if (vehicle.model) models.add(vehicle.model);
    });
    return {
      years: Array.from(years).sort().map(year => ({ value: year, label: year.toString() })),
      makes: Array.from(makes).sort().map(make => ({ value: make, label: make })),
      models: Array.from(models).sort().map(model => ({ value: model, label: model })),
    };
  }, [dataSource]);

  // Update column definitions with filter options
  const updatedColumns = useMemo(() => {
    return columns.map(col => {
      if (col.accessorKey === 'year' && filterOptions.years.length > 0) {
        return {
          ...col,
          filterSelectOptions: filterOptions.years,
        };
      } else if (col.accessorKey === 'make' && filterOptions.makes.length > 0) {
        return {
          ...col,
          filterSelectOptions: filterOptions.makes,
        };
      } else if (col.accessorKey === 'model' && filterOptions.models.length > 0) {
        return {
          ...col,
          filterSelectOptions: filterOptions.models,
        };
      }
      return col;
    });
  }, [filterOptions]);

  // Prepare data for the table
  const data = useMemo(() => {
    const newdata = [];
    if (!dataSource || dataSource.length === 0) return newdata;
    const deptIds = (Array.isArray(departmentIds) && departmentIds.length > 0)
      ? departmentIds.map(id => parseInt(id)).filter(id => !isNaN(id))
      : [parseInt(department_id || work_flow_id)];
    const matchesDept = o => deptIds.includes(o.department_id);
    dataSource.forEach((vehicle) => {
      let total_time = 0;
      let entries = 0;
      let completed_departments = _.filter(vehicle.completed_data_steps, matchesDept);
      if (completed_departments.length < 1) {
        completed_departments = _.filter(vehicle.completed_departments, matchesDept);
      }
      completed_departments.forEach((completed_department) => {
        if (matchesDept(completed_department)) {
          total_time += completed_department.recon_total_24_7 || 0;
          entries += 1;
        }
      });
      completed_departments.forEach((completed_department) => {
        const recon_total_24_7 = completed_department.recon_total_24_7 || 0;
        newdata.push({
          stock: vehicle.stock,
          year: vehicle.year || '',
          make: vehicle.make || '',
          model: vehicle.model || '',
          starttime: completed_department.recon_starttime,
          endtime: completed_department.recon_endtime,
          time: convertSecondsToTime(recon_total_24_7),
          time_seconds: recon_total_24_7,
          total: convertSecondsToTime(total_time),
          total_seconds: total_time,
          entries,
          vehicle: [vehicle],
        });
      });
    });
    return newdata;
  }, [dataSource, department_id, work_flow_id, departmentIds]);

  const handleExportRowsToCSV = (rows) => {
    if (!rows || rows.length === 0) return;
    const rowData = rows.map(row => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = row.original[key];
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportDataToCSV = () => {
    if (!data || data.length === 0) return;
    const rowData = data.map(item => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = item[key];
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportRowsToPDF = (rows) => {
    if (!rows || rows.length === 0) return;
    const doc = new jsPDF();
    const tableData = rows.map(row => exportableColumnKeys.map(key => row.original[key]));
    const tableHeaders = exportableColumnKeys.map(key => columns.find(col => col.accessorKey === key).header);
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });
    doc.save(filename);
  };

  const table = useMaterialReactTable({
    columns: updatedColumns,
    data,
    enableRowSelection: true,
    enableExpandAll: true,
    renderDetailPanel,
    positionToolbarAlertBanner: 'bottom',
    enableSorting: true,
    enableColumnFilters: true,
    enableFilters: true,
    enableGlobalFilter: true,
    initialState: {
      sorting: [{ id: 'time', desc: false }],
      showColumnFilters: true,
    },
    muiTableBodyCellProps: {
      sx: {
        padding: '6px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '6px',
      },
    },
    muiTableHeadCellFilterTextFieldProps: {
      variant: 'outlined',
      size: 'small',
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          padding: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRowsToCSV(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export to CSV
        </Button>
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRowsToCSV(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows to CSV
        </Button>
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRowsToPDF(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export to PDF
        </Button>
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRowsToPDF(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows to PDF
        </Button>
      </Box>
    ),
  });

  return (
    <ThemeProvider theme={tableTheme}>
      <MaterialReactTable table={table} key={JSON.stringify(filterOptions)} />
    </ThemeProvider>
  );
};

export default ReportVehicles;